/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetOrganizationRemoteSessionIssuerDeletePreflightRequest,
  GetOrganizationRemoteSessionIssuerDeletePreflightSecurity,
} from "../models/operations/getorganizationremotesessionissuerdeletepreflight.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildOrganizationRemoteSessionIssuerDeletePreflightQuery,
  OrganizationRemoteSessionIssuerDeletePreflightQueryData,
  prefetchOrganizationRemoteSessionIssuerDeletePreflight,
  queryKeyOrganizationRemoteSessionIssuerDeletePreflight,
} from "./organizationRemoteSessionIssuerDeletePreflight.core.js";
export {
  buildOrganizationRemoteSessionIssuerDeletePreflightQuery,
  type OrganizationRemoteSessionIssuerDeletePreflightQueryData,
  prefetchOrganizationRemoteSessionIssuerDeletePreflight,
  queryKeyOrganizationRemoteSessionIssuerDeletePreflight,
};

export type OrganizationRemoteSessionIssuerDeletePreflightQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getIssuerDeletePreflight organizationRemoteSessionIssuers
 *
 * @remarks
 * Authoritative impact summary for deleting a remote_session_issuer: associated client count and affected MCP server names. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerDeletePreflight(
  request: GetOrganizationRemoteSessionIssuerDeletePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionIssuerDeletePreflightSecurity
    | undefined,
  options?: QueryHookOptions<
    OrganizationRemoteSessionIssuerDeletePreflightQueryData,
    OrganizationRemoteSessionIssuerDeletePreflightQueryError
  >,
): UseQueryResult<
  OrganizationRemoteSessionIssuerDeletePreflightQueryData,
  OrganizationRemoteSessionIssuerDeletePreflightQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildOrganizationRemoteSessionIssuerDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getIssuerDeletePreflight organizationRemoteSessionIssuers
 *
 * @remarks
 * Authoritative impact summary for deleting a remote_session_issuer: associated client count and affected MCP server names. Requires org:read.
 */
export function useOrganizationRemoteSessionIssuerDeletePreflightSuspense(
  request: GetOrganizationRemoteSessionIssuerDeletePreflightRequest,
  security?:
    | GetOrganizationRemoteSessionIssuerDeletePreflightSecurity
    | undefined,
  options?: SuspenseQueryHookOptions<
    OrganizationRemoteSessionIssuerDeletePreflightQueryData,
    OrganizationRemoteSessionIssuerDeletePreflightQueryError
  >,
): UseSuspenseQueryResult<
  OrganizationRemoteSessionIssuerDeletePreflightQueryData,
  OrganizationRemoteSessionIssuerDeletePreflightQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildOrganizationRemoteSessionIssuerDeletePreflightQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setOrganizationRemoteSessionIssuerDeletePreflightData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    },
  ],
  data: OrganizationRemoteSessionIssuerDeletePreflightQueryData,
): OrganizationRemoteSessionIssuerDeletePreflightQueryData | undefined {
  const key = queryKeyOrganizationRemoteSessionIssuerDeletePreflight(
    ...queryKeyBase,
  );

  return client.setQueryData<
    OrganizationRemoteSessionIssuerDeletePreflightQueryData
  >(key, data);
}

export function invalidateOrganizationRemoteSessionIssuerDeletePreflight(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      id: string;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "getDeletePreflight",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllOrganizationRemoteSessionIssuerDeletePreflight(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "organizationRemoteSessionIssuers",
      "getDeletePreflight",
    ],
  });
}
